// @ts-nocheck
import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import NotificationAlert from '../../shared/components/NotificationAlert';
import { listHospitals, getHospital } from '../../services/hospitalService';

/**
 * Reusable dialog for creating or editing a user.
 * Props:
 * - open, onClose, onSave
 * - user: existing user object (null/undefined for create)
 * - mode: 'create' | 'edit' (default 'edit')
 */
/**
 * @param {{open?: boolean, onClose?: (...args: any[]) => void, onExited?: (...args: any[]) => void, user?: any, onSave?: (user: any) => void, mode?: ('create'|'edit')}} props
 */
export default function UserEditDialog({ open, onClose, onExited, user = {}, onSave, mode = 'edit', error = null, onErrorClear, saving = false, fixedHospital = false }) {
  const isCreate = mode === 'create';
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [isAdmin, setIsAdmin] = useState(!!user?.isAdmin);
  const [isDemo, setIsDemo] = useState(!!user?.isDemo);
  const [isOffline, setIsOffline] = useState(!!user?.isOffline);
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfaEnabled ?? (mode === 'create'));
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [hospitalId, setHospitalId] = useState(user?.hospitalId || '');
  const [hospitalInputValue, setHospitalInputValue] = useState(user?.hospitalName || '');
  const [hospitalOptions, setHospitalOptions] = useState([]);
  const [hospitalLoading, setHospitalLoading] = useState(false);
  const [hospitalError, setHospitalError] = useState('');

  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const requiresHospital = !isAdmin && !isDemo;

  function generateStrongPassword(length = 16) {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()-_=+[]{};:,.<>?";

    const all = upper + lower + numbers + symbols;

    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);

    // ensure one of each category
    const pwChars = [];
    const randFrom = (str) => str[Math.floor(Math.random() * str.length)];
    pwChars.push(randFrom(upper));
    pwChars.push(randFrom(lower));
    pwChars.push(randFrom(numbers));
    pwChars.push(randFrom(symbols));

    for (let i = pwChars.length; i < length; i++) {
      pwChars.push(all[array[i] % all.length]);
    }

    // shuffle array
    for (let i = pwChars.length - 1; i > 0; i--) {
      const j = Math.floor(window.crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000 * (i + 1));
      [pwChars[i], pwChars[j]] = [pwChars[j], pwChars[i]];
    }

    return pwChars.join('');
  }

  const handleGeneratePassword = () => {
    const pw = generateStrongPassword();
    setPassword(pw);
    setShowPassword(true);
    setPasswordError(validatePassword(pw));
  };

  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setEmail(user?.email ?? '');
    setIsAdmin(!!user?.isAdmin);
    setIsDemo(!!user?.isDemo);
    setIsOffline(!!user?.isOffline);
    setMfaEnabled(user?.mfaEnabled ?? (mode === 'create'));
    setPassword('');
    setHospitalId(user?.hospitalId || '');
    setHospitalInputValue(user?.hospitalName || '');

    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setPasswordError('');
    setHospitalError('');
  }, [user, open, fixedHospital]);

  const sanitizeName = (name) => name.replace(/[^a-zA-Z '\-]/g, '');

  const getNormalizedFlags = (flag, checked) => {
    if (flag === 'admin') {
      return checked
        ? { isAdmin: true, isDemo: false, isOffline: false }
        : { isAdmin: false, isDemo, isOffline };
    }

    if (flag === 'demo') {
      return checked
        ? { isAdmin: false, isDemo: true, isOffline: false }
        : { isAdmin, isDemo: false, isOffline: false };
    }

    if (flag === 'offline') {
      return checked
        ? { isAdmin: false, isDemo: true, isOffline: true }
        : { isAdmin, isDemo: isDemo, isOffline: false };
    }

    return { isAdmin, isDemo, isOffline };
  };

  const handleFlagChange = (flag, checked) => {
    const normalized = getNormalizedFlags(flag, checked);
    setIsAdmin(normalized.isAdmin);
    setIsDemo(normalized.isDemo);
    setIsOffline(normalized.isOffline);
    if (normalized.isDemo) setMfaEnabled(false);
  };

  const isAdminFlagDisabled = isDemo || isOffline;
  const isDemoFlagDisabled = isAdmin || mfaEnabled;
  const isOfflineFlagDisabled = !isDemo || isAdmin;

  const handleSave = () => {
    const base = user || {};
    const updated = {
      ...base,
      firstName: sanitizeName(firstName).trim(),
      lastName: sanitizeName(lastName).trim(),
      email: email.trim(),
      isAdmin: !!isAdmin,
      isDemo: !!isDemo,
      isOffline: !!isOffline,
      mfaEnabled: !!mfaEnabled,
      hospitalId: requiresHospital ? hospitalId : '',
      hospitalName: requiresHospital ? hospitalInputValue.trim() : '',
    };
    if (isCreate || password) {
      updated.password = password;
    }
    onSave && onSave(updated);
  };

  const validateFirstName = (value) => {
    const v = value.trim();
    if (!v) return 'First name is required';
    if (!/^[a-zA-Z '\-]+$/.test(v))
      return 'Only letters, spaces, hyphens and apostrophes allowed';
    return '';
  };

  const validateLastName = (value) => {
    const v = value.trim();
    if (!v) return 'Last name is required';
    if (!/^[a-zA-Z '\-]+$/.test(v))
      return 'Only letters, spaces, hyphens and apostrophes allowed';
    return '';
  };

  const validateEmail = (value) => {
    const v = value.trim();
    if (!v) return 'Email is required';
    // simple email regex
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(v)) return 'Invalid email address';
    return '';
  };

  useEffect(() => {
    if (!open) {
      setHospitalOptions([]);
      setHospitalLoading(false);
      return;
    }

    if (!requiresHospital) {
      setHospitalOptions([]);
      return;
    }

    if (!hospitalId && !(hospitalInputValue || '').trim()) {
      setHospitalOptions([]);
      return;
    }

    let active = true;
    const timer = setTimeout(async () => {
      if (hospitalId) {
        try {
          const selected = await getHospital(hospitalId);
          if (active && selected) {
            setHospitalOptions([{ id: selected.id, ...selected }]);
            setHospitalInputValue(selected.name || '');
          }
        } catch (err) {
          console.warn('Error loading selected hospital:', err);
        }
      }

      const queryText = (hospitalInputValue || '').trim();
      if (!queryText) {
        if (active) {
          setHospitalOptions((currentOptions) => {
            if (!hospitalId) return [];

            const selectedOption = currentOptions.find((option) => option.id === hospitalId);
            if (selectedOption) return [selectedOption];

            if (!hospitalInputValue) return [];
            return [{ id: hospitalId, name: hospitalInputValue }];
          });
        }
        return;
      }

      setHospitalLoading(true);
      try {
        const { hospitals } = await listHospitals({ page: 0, pageSize: 20, nameFilter: queryText, nameFilterOperator: 'startsWith' });
        if (active) {
          setHospitalOptions((hospitals || []).map((h) => ({ ...h, name: h.name || '' })));
        }
      } catch (err) {
        console.error('Error searching hospitals:', err);
        if (active) setHospitalOptions([]);
      } finally {
        if (active) setHospitalLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, requiresHospital, hospitalId, hospitalInputValue]);

  useEffect(() => {
    if (!hospitalOptions.length || !hospitalId) return;

    const selectedHospital = hospitalOptions.find((option) => option.id === hospitalId);
    if (selectedHospital) {
      setHospitalInputValue(selectedHospital.name || '');
    }
  }, [hospitalOptions, hospitalId]);

  const validateHospital = (value) => {
    if (!requiresHospital) return '';
    if (!value) return 'Hospital is required';
    if (!hospitalOptions.some((h) => h.id === value)) return 'Select a valid hospital';
    return '';
  };

  useEffect(() => {
    if (!requiresHospital) {
      setHospitalError('');
    } else if (!hospitalId) {
      setHospitalError('Hospital is required');
    }
  }, [requiresHospital, hospitalId]);

  const validatePassword = (value) => {
    if (!value) {
      if (isCreate) return 'Password is required';
      return '';
    }
    // at least 8 chars, upper, lower, number, special (symbols matching generator)
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+\[\]{};:,.<>?]).{8,}$/;
    if (!re.test(value))
      return 'Password must be at least eight characters and include uppercase, lowercase, numeric, and special characters.';
    return '';
  };

  const handleFirstNameChange = (e) => {
    const v = e.target.value;
    setFirstName(v);
    setFirstNameError(validateFirstName(v));
  };

  const handleLastNameChange = (e) => {
    const v = e.target.value;
    setLastName(v);
    setLastNameError(validateLastName(v));
  };

  const handleEmailChange = (e) => {
    const v = e.target.value;
    setEmail(v);
    setEmailError(validateEmail(v));
  };

  const handlePasswordChange = (e) => {
    const v = e.target.value;
    setPassword(v);
    setPasswordError(validatePassword(v));
  };

  const selectedHospitalOption = hospitalId
    ? hospitalOptions.find((item) => item.id === hospitalId) || { id: hospitalId, name: hospitalInputValue }
    : null;

  const isSaveDisabled = () => {
    if (firstNameError || lastNameError || emailError || passwordError || hospitalError) return true;
    if (!firstName.trim() || !lastName.trim() || !email.trim()) return true;
    if (requiresHospital && !hospitalId.trim()) return true;
    if (isCreate && !password) return true;
    return false;
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onExited }} disableScrollLock>
      <DialogTitle>{isCreate ? 'Add user' : 'Edit user'}</DialogTitle>
      <DialogContent dividers>
        <NotificationAlert error={error} onClear={onErrorClear} />
        <Stack spacing={2} sx={{ mt: error ? 2 : 0 }}>
          <TextField
            label="First name"
            value={firstName}
            onChange={handleFirstNameChange}
            error={!!firstNameError}
            helperText={firstNameError}
            fullWidth
            autoFocus
          />
          <TextField
            label="Last name"
            value={lastName}
            onChange={handleLastNameChange}
            error={!!lastNameError}
            helperText={lastNameError}
            fullWidth
          />
          <TextField
            label="Email"
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
            fullWidth
          />
          <TextField
            type={showPassword ? 'text' : 'password'}
            label={isCreate ? 'Password' : 'New password (optional)'}
            value={password}
            onChange={handlePasswordChange}
            error={!!passwordError}
            helperText={passwordError}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ display: 'flex' }}>
                  <IconButton
                    aria-label="Generate password"
                    onClick={handleGeneratePassword}
                    edge="end"
                    sx={{ mr: 0.1 }}
                  >
                    <VpnKeyIcon />
                  </IconButton>
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
            {!fixedHospital && (
              <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  disabled={isAdminFlagDisabled}
                  control={
                    <Switch
                      checked={isAdmin}
                      onChange={(e) => handleFlagChange('admin', e.target.checked)}
                      disabled={isAdminFlagDisabled}
                    />
                  }
                  label="Administrator"
                />
                <FormControlLabel
                  sx={{ m: 0 }}
                  disabled={isDemoFlagDisabled}
                  control={
                    <Switch
                      checked={isDemo}
                      onChange={(e) => handleFlagChange('demo', e.target.checked)}
                      disabled={isDemoFlagDisabled}
                    />
                  }
                  label="Demo user"
                />
                <FormControlLabel
                  sx={{ m: 0 }}
                  disabled={isOfflineFlagDisabled}
                  control={
                    <Switch
                      checked={isOffline}
                      onChange={(e) => handleFlagChange('offline', e.target.checked)}
                      disabled={isOfflineFlagDisabled}
                    />
                  }
                  label="Offline user"
                />
              </Stack>
            )}
            <FormControlLabel
              sx={{ m: 0 }}
              disabled={isDemo}
              control={
                <Switch
                  checked={mfaEnabled}
                  onChange={(e) => setMfaEnabled(e.target.checked)}
                  disabled={isDemo}
                />
              }
              label="MFA"
            />
          {requiresHospital && (
            <Autocomplete
              options={hospitalOptions}
              value={selectedHospitalOption}
              disabled={fixedHospital}
              onChange={(_, newValue, reason) => {
                if (fixedHospital) return;

                if (reason === 'blur' && !newValue && hospitalId) {
                  return;
                }

                const newHospitalId = (newValue && newValue.id) || '';
                const newHospitalName = (newValue && newValue.name) || '';
                setHospitalId(newHospitalId);
                setHospitalInputValue(newHospitalName);
                setHospitalError(validateHospital(newHospitalId));
              }}
              inputValue={hospitalInputValue}
              onInputChange={(_, newInputValue, reason) => {
                if (fixedHospital) return;
                setHospitalInputValue(newInputValue);

                if (reason === 'clear') {
                  setHospitalId('');
                  setHospitalError(validateHospital(''));
                  return;
                }

                if (reason !== 'input') {
                  return;
                }

                const matchedHospital = hospitalOptions.find((option) => option.name === newInputValue);
                const nextHospitalId = matchedHospital?.id || '';
                setHospitalId(nextHospitalId);
                setHospitalError(validateHospital(nextHospitalId));
              }}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={hospitalLoading}
              noOptionsText={hospitalInputValue ? 'No matching hospitals' : 'Type to search hospitals'}
              freeSolo={false}
              fullWidth
              renderOption={(props, option) => (
                <li {...props}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Hospital"
                  error={!!hospitalError}
                  helperText={hospitalError}
                  fullWidth
                />
              )}
              sx={{
                '& .MuiAutocomplete-endAdornment': {
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  '& .MuiIconButton-root': {
                    padding: '4px',
                    width: 30,
                    height: 30,
                    minWidth: 30,
                  },
                },
                '& .MuiInputAdornment-root': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                },
                '& .MuiInputBase-input': {
                  padding: '12px 12px',
                },
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={isSaveDisabled() || saving} variant="contained" sx={{ minWidth: 96 }}>
          {saving ? <CircularProgress size={18} color="inherit" /> : (isCreate ? 'Create' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
